import { useMemo, useState } from "react";
import { X, Minus, Plus, ShoppingCart, AlertCircle } from "lucide-react";
import { MenuItem } from "@/hooks/usePublicMenu";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart, CartItem } from "@/contexts/CartContext";
import { useFormatPrice } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  buildFeatureSummary,
  buildLegacyCustomization,
  calculateSelectedFeaturesPrice,
  getFeatureGroups,
  getFeatureId,
  getFeatureOptionLabel,
  getGroupRuleText,
  mapSelectedFeatures,
  validateFeatureSelections,
} from "@/lib/product-features";

 type Props = {
  item: MenuItem;
  onClose: () => void;
  availableTags?: { key: string; label: string }[];
};

export default function ItemDetailModal({ item, onClose , availableTags = []}: Props) {
  const { t, language } = useLanguage();
  const { addItem } = useCart();
  const formatPrice = useFormatPrice();
  const { toast } = useToast();
  const groups = useMemo(() => getFeatureGroups(item, language), [item, language]);
  const isAr = language === "ar";

  const [selectedByGroup, setSelectedByGroup] = useState<Record<string, string[]>>({});
  const [removedItems, setRemovedItems] = useState<string[]>([]);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [submitted, setSubmitted] = useState(false);

  const name = language === "ar" ? item.nameAr : item.name;
  const desc = language === "ar" ? item.descriptionAr : item.description;

  const selectedFeatures = useMemo(
    () => mapSelectedFeatures(groups, selectedByGroup),
    [groups, selectedByGroup],
  );
  const featureErrors = useMemo(
    () => validateFeatureSelections(groups, selectedByGroup, language),
    [groups, selectedByGroup, language],
  );
  const selectedFeaturesAdd = useMemo(
    () => calculateSelectedFeaturesPrice(selectedFeatures),
    [selectedFeatures],
  );
  const unitPrice = item.price + selectedFeaturesAdd;
  const totalPrice = unitPrice * quantity;

    const getLabel = (tagKey: string) =>
    availableTags.find((t) => t.key === tagKey)?.label ?? tagKey;

  const toggleOption = (groupKey: string, optionId: string, selectionMode: "single" | "multi", max: number) => {
    setSelectedByGroup((prev) => {
      const current = prev[groupKey] || [];

      if (selectionMode === "single") {
        return { ...prev, [groupKey]: current[0] === optionId ? [] : [optionId] };
      }

      if (current.includes(optionId)) {
        return { ...prev, [groupKey]: current.filter((id) => id !== optionId) };
      }

      if (Number.isFinite(max) && current.length >= max) {
        return prev;
      }

      return { ...prev, [groupKey]: [...current, optionId] };
    });
  };

  const toggleRemoved = (ingredient: string) => {
    setRemovedItems((prev) =>
      prev.includes(ingredient) ? prev.filter((r) => r !== ingredient) : [...prev, ingredient],
    );
  };

  const handleAddToCart = () => {
    setSubmitted(true);
    
    const errorKeys = Object.keys(featureErrors);
    if (errorKeys.length > 0) {
      // Get the names of missing required features
      const missingFeatures = errorKeys.map(key => {
        const group = groups.find(g => g.key === key);
        return group?.label || key;
      });
      
      // Show toast with missing features
      toast({
        variant: "destructive",
        title: isAr ? "اختيارات مطلوبة" : "Required selections",
        description: isAr 
          ? `الرجاء إكمال الاختيارات التالية: ${missingFeatures.join("، ")}`
          : `Please complete the following: ${missingFeatures.join(", ")}`,
      });
      
      // Scroll to the first error
      const firstErrorGroup = document.querySelector(`[data-group-key="${errorKeys[0]}"]`);
      firstErrorGroup?.scrollIntoView({ behavior: "smooth", block: "center" });
      
      return;
    }

    const featureSummary = buildFeatureSummary(selectedFeatures, language);
    const legacyCustomization = buildLegacyCustomization(selectedFeatures);

    const cartItem: CartItem = {
      id: "",
      menuItem: item,
      quantity,
      size: legacyCustomization.size || undefined,
      extras: legacyCustomization.extras,
      removed: removedItems,
      specialInstructions,
      unitPrice,
      selectedFeatures,
      featureSummary,
    };

    addItem(cartItem);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-border bg-card shadow-2xl sm:rounded-2xl">
        <button
          onClick={onClose}
          className="absolute end-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-background/80 text-foreground transition-colors hover:bg-muted"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <img src={item.image} alt={name} className="h-52 w-full object-cover sm:h-64" />

        <div className="space-y-5 p-5">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground">{name}</h2>
            <p className="mt-1 text-muted-foreground">{desc}</p>
            {item.tags.length > 0 && (
              <div className="mt-2 flex gap-1">
                {item.tags.map((tag) => (
                  <Badge key={tag.key} variant="outline" className="text-xs">
                    {getLabel(tag.key)}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {groups.map((group) => {
            const selectedIds = selectedByGroup[group.key] || [];
            const hasError = submitted && featureErrors[group.key];

            return (
              <div 
                key={group.key} 
                data-group-key={group.key}
                className={`space-y-2 rounded-lg p-3 -mx-3 transition-colors ${hasError ? "bg-destructive/10 border border-destructive/30" : ""}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className={`font-heading font-semibold ${hasError ? "text-destructive" : "text-foreground"}`}>
                      {group.label}
                      {hasError && <AlertCircle className="inline-block h-4 w-4 ms-1" />}
                    </h3>
                    <p className="text-xs text-muted-foreground">{getGroupRuleText(group, language)}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  {group.options.map((option, index) => {
                    const optionId = getFeatureId(option, index);
                    const isSelected = selectedIds.includes(optionId);
                    const label = getFeatureOptionLabel(option, language);

                    return (
                      <label
                        key={optionId}
                        className="flex cursor-pointer items-center justify-between rounded-lg border border-border p-3 transition-colors hover:bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleOption(group.key, optionId, group.selectionMode, group.max)}
                          />
                          <div>
                            <span className="text-sm text-foreground">{label}</span>
                          </div>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {Number(option.price || 0) > 0 ? `+${formatPrice(Number(option.price || 0))}` : formatPrice(0)}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {hasError && <p className="text-sm text-destructive">{featureErrors[group.key]}</p>}
              </div>
            );
          })}

          {item.removable && item.removable.length > 0 && (
            <div>
              <h3 className="mb-2 font-heading font-semibold text-foreground">{t("menu.remove")}</h3>
              <div className="flex flex-wrap gap-2">
                {item.removable.map((ingredient) => (
                  <button
                    key={ingredient}
                    onClick={() => toggleRemoved(ingredient)}
                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      removedItems.includes(ingredient)
                        ? "border-destructive bg-destructive text-destructive-foreground line-through"
                        : "border-border text-foreground hover:bg-muted"
                    }`}
                  >
                    {ingredient}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="mb-2 font-heading font-semibold text-foreground">{t("menu.specialInstructions")}</h3>
            <Textarea
              placeholder="e.g. No onions, extra sauce..."
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              className="resize-none"
              rows={2}
            />
          </div>

          <div className="flex items-center gap-4 pt-2">
            <div className="flex items-center gap-3 rounded-lg border border-border p-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                <Minus className="h-4 w-4" />
              </Button>
              <span className="w-8 text-center font-bold text-foreground">{quantity}</span>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setQuantity(quantity + 1)}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <Button className="flex-1 gap-2 rounded-xl py-6 text-base font-bold" onClick={handleAddToCart}>
              <ShoppingCart className="h-5 w-5" />
              {t("menu.addToCart")} · {formatPrice(totalPrice)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
